import { Button } from "melodies-source/Button";
import { Modal } from "melodies-source/Modal";
import { Body1 } from "melodies-source/Text";
import styled from "styled-components";

interface Props {
  isOpen: boolean;
  /** Venue names of the selected shows that fall outside Sony territories. */
  venues: string[];
  /** Dismiss without duplicating (go back to the selection). */
  onCancel: () => void;
  /** Proceed with the duplication anyway (warn-only, never blocks). */
  onConfirm: () => void;
}

/**
 * Warn-only popup shown before duplicating onto shows whose venues aren't
 * covered Sony territories — the Sony opt-in (and the "sony" record label) won't
 * be included for those shows. Mirrors the creator wizard's warning; territory
 * detection is shared via @max/common/creator's `isNonSonyTerritory`. Never
 * blocks: "Duplicate anyway" proceeds.
 */
export const SonyTerritoryWarningModal = ({
  isOpen,
  venues,
  onCancel,
  onConfirm,
}: Props) => {
  const single = venues.length === 1;
  return (
    <Modal
      header="Some shows aren't covered by Sony"
      isOpen={isOpen}
      onClose={onCancel}
      withCloseIcon
    >
      <Container>
        <Body1>
          {single ? "This show isn't" : "Some of the shows you selected aren't"}{" "}
          in a Sony-covered territory, so the Sony opt-in won't be included{" "}
          {single ? "for it" : "for them"}:
        </Body1>
        <List>
          {venues.map((venue, index) => (
            <ListItem key={index}>{venue}</ListItem>
          ))}
        </List>
        <Footer>
          <Button variant="tertiary" onClick={onCancel}>
            Go back
          </Button>
          <Button onClick={onConfirm}>Duplicate anyway</Button>
        </Footer>
      </Container>
    </Modal>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding-left: 20px;
`;

const ListItem = styled.li`
  font-weight: 600;
`;

const Footer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
  justify-content: flex-end;
`;
